import { toast } from "./ui/Toast";

/** Whether this is running from the home screen rather than in a browser tab.
 *  iOS answers the first question and not the second; every other platform is
 *  the other way round, so both are asked. */
export function isStandalone(): boolean {
  return (
    (navigator as unknown as { standalone?: boolean }).standalone === true ||
    window.matchMedia("(display-mode: standalone)").matches ||
    window.matchMedia("(display-mode: minimal-ui)").matches
  );
}

export function isIOS(): boolean {
  return (
    /iphone|ipad|ipod/i.test(navigator.userAgent) ||
    // iPadOS reports itself as a Mac, and is the one Mac with a touch screen.
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)
  );
}

/** The number on the home screen icon.
 *
 *  Set from two places and for two different reasons. The service worker sets
 *  it from a push, which is the only thing that can move it while the app is
 *  shut. This one runs when the list is on screen, and is what corrects the
 *  drift: nothing pushes when work is finished on the laptop, so the number a
 *  phone is carrying can be stale by the time it is picked up, and opening the
 *  list has to be able to put it right.
 *
 *  Zero clears it — the spec defines setAppBadge(0) that way — so being caught
 *  up needs no separate call.
 *
 *  Asked for rather than assumed, and failing quietly, like the registration
 *  below. Only an installed app has an icon to draw a number on; in a tab the
 *  call is missing on most browsers and refused on the rest, and neither is
 *  worth a word to the person holding the phone. */
export function setBadge(count: number): void {
  const nav = navigator as Navigator & { setAppBadge?: (count?: number) => Promise<void> };
  if (typeof nav.setAppBadge !== "function") return;
  void nav.setAppBadge(Math.max(0, Math.trunc(count))).catch(() => {});
}

/** Registers the worker that makes a cold launch instant and an offline one
 *  possible, and says so when a newer build is sitting ready.
 *
 *  Not in dev: a worker in front of the dev server intercepts the module graph
 *  and hot reload stops telling the truth. */
export function registerServiceWorker(): void {
  if (!import.meta.env.PROD) return;
  if (!("serviceWorker" in navigator)) return;

  window.addEventListener("load", () => {
    void navigator.serviceWorker
      .register("/sw.js")
      .then((registration) => {
        const offer = (worker: ServiceWorker | null) => {
          // A worker sitting in "installed" while another one controls the page
          // is a deploy that landed while the app was open. Nothing is swapped
          // underneath a half-written draft: the app says so and waits to be
          // told. Ignored, it takes hold the next time the app is launched.
          if (!worker || !navigator.serviceWorker.controller) return;
          toast("A newer version is ready.", "plain", 10000, {
            label: "Reload",
            run: () => worker.postMessage("skip-waiting"),
          });
        };

        offer(registration.waiting);
        registration.addEventListener("updatefound", () => {
          const fresh = registration.installing;
          fresh?.addEventListener("statechange", () => {
            if (fresh.state === "installed") offer(fresh);
          });
        });
      })
      .catch(() => {
        /* An app that works offline is a bonus; failing to set one up is not
           worth a word to the person holding the phone. */
      });

    // Only ever reached because the update above was accepted.
    let reloading = false;
    navigator.serviceWorker.addEventListener("controllerchange", () => {
      if (reloading) return;
      reloading = true;
      window.location.reload();
    });
  });
}
